import { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Building2,
  FolderKanban,
  ListTodo,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Eye,
  CheckCircle,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Users,
  DollarSign,
  Calendar,
  FileText,
  Download,
  ChevronRight,
  Wrench,
  Zap,
  HardHat,
  Droplets,
  ClipboardCheck,
  GraduationCap,
  FileCheck,
  Sparkles,
  Truck,
  Package,
  ScrollText,
  Shield,
  BarChart3,
  ArrowLeft,
  MessageSquare,
  Send,
  Lock,
  Upload,
  File,
  Image,
  FileSpreadsheet,
  Presentation,
  FileArchive,
  AtSign,
  ArrowRightLeft,
  Mail,
  UserPlus,
  Copy,
  ExternalLink,
  Activity,
  Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import ProjectStatusUpdates from "@/components/admin/ProjectStatusUpdates";
import EntityDetailModal from "@/components/admin/EntityDetailModal";
import SendUpdateEmailModal from "@/components/admin/SendUpdateEmailModal";
import ChangeOrdersTab from "@/components/admin/ChangeOrdersTab";
import PayApplicationsTab from "@/components/admin/PayApplicationsTab";
import SubmittalsTab from "@/components/admin/SubmittalsTab";
import RfisTab from "@/components/admin/RfisTab";
import FieldLogsTab from "@/components/admin/FieldLogsTab";
import IcraPermitsTab from "@/components/admin/IcraPermitsTab";
import StandardsCompass from "@/components/admin/StandardsCompass";
import ProjectProcurementTab from "@/components/admin/ProjectProcurementTab";

// Category Icons and Labels
const CATEGORY_CONFIG = {
  plumbing_prework: { icon: Droplets, label: "Plumbing Pre-work", color: "text-blue-400 bg-blue-500/10" },
  electrical_prework: { icon: Zap, label: "Electrical Pre-work", color: "text-yellow-400 bg-yellow-500/10" },
  construction_containment: { icon: HardHat, label: "Construction/Containment", color: "text-orange-400 bg-orange-500/10" },
  ro_system_installation: { icon: Droplets, label: "RO System Installation", color: "text-cyan-400 bg-cyan-500/10" },
  inspections: { icon: ClipboardCheck, label: "Inspections", color: "text-purple-400 bg-purple-500/10" },
  staff_training: { icon: GraduationCap, label: "Staff Training", color: "text-green-400 bg-green-500/10" },
  project_closeout: { icon: FileCheck, label: "Project Closeout", color: "text-emerald-400 bg-emerald-500/10" },
  terminal_cleaning: { icon: Sparkles, label: "Terminal Cleaning", color: "text-pink-400 bg-pink-500/10" },
  labor: { icon: Users, label: "Labor", color: "text-slate-400 bg-slate-500/10" },
  travel_expenses: { icon: Truck, label: "Travel Expenses", color: "text-amber-400 bg-amber-500/10" },
  materials: { icon: Package, label: "Materials", color: "text-indigo-400 bg-indigo-500/10" },
  documentation: { icon: ScrollText, label: "Documentation", color: "text-teal-400 bg-teal-500/10" }
};

const ORG_TYPE_CONFIG = {
  hospital_client: { label: "Hospital Client", color: "text-blue-400 bg-blue-500/10" },
  external_contractor: { label: "External Contractor", color: "text-orange-400 bg-orange-500/10" },
  internal_contractor: { label: "Internal Contractor", color: "text-green-400 bg-green-500/10" },
  vendor: { label: "Vendor", color: "text-purple-400 bg-purple-500/10" }
};

// Status Badge Component
const StatusBadge = ({ status, type = "project" }) => {
  const projectStyles = {
    planning: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    in_progress: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    on_hold: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    completed: "bg-green-500/10 text-green-400 border-green-500/20",
    cancelled: "bg-red-500/10 text-red-400 border-red-500/20"
  };
  
  const taskStyles = {
    pending: "bg-slate-500/10 text-slate-400 border-slate-500/20",
    in_progress: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    completed: "bg-green-500/10 text-green-400 border-green-500/20",
    on_hold: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    blocked: "bg-red-500/10 text-red-400 border-red-500/20"
  };

  const styles = type === "project" ? projectStyles : taskStyles;

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
      {status?.replace(/_/g, ' ').toUpperCase()}
    </span>
  );
};

// Priority Badge
const PriorityBadge = ({ priority }) => {
  const styles = {
    low: "bg-slate-500/10 text-slate-400",
    normal: "bg-blue-500/10 text-blue-400",
    high: "bg-orange-500/10 text-orange-400",
    urgent: "bg-red-500/10 text-red-400"
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[priority] || styles.normal}`}>
      {priority?.toUpperCase()}
    </span>
  );
};

// Stats Card
const StatsCard = ({ title, value, icon: Icon, color, subtext }) => (
  <div className="card-clinical p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-slate-500 text-sm">{title}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
        {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

// =====================
// PROJECT DASHBOARD
// =====================
const ProjectDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/projects/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const projectStats = stats?.project_stats || {};
  const taskStats = stats?.task_stats || {};
  const budgetSummary = stats?.budget_summary || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FolderKanban className="w-7 h-7 text-blue-500" />
            Project Management
          </h1>
          <p className="text-slate-400 text-sm mt-1">Water purification project tracking & management</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate("/admin/projects/organizations")}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Organizations
          </Button>
          <Button 
            size="sm" 
            onClick={() => navigate("/admin/projects/new")}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </div>
      </div>

      {/* HIPAA Compliance Banner */}
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center gap-3">
        <Shield className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        <div>
          <p className="text-emerald-400 text-sm font-medium">HIPAA Compliant</p>
          <p className="text-emerald-400/70 text-xs">All access is logged and audited for compliance</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Active Projects"
          value={(projectStats.planning || 0) + (projectStats.in_progress || 0)}
          icon={FolderKanban}
          color="bg-blue-500/10 text-blue-400"
        />
        <StatsCard
          title="Completed"
          value={projectStats.completed || 0}
          icon={CheckCircle}
          color="bg-green-500/10 text-green-400"
        />
        <StatsCard
          title="Tasks In Progress"
          value={taskStats.in_progress || 0}
          icon={Clock}
          color="bg-purple-500/10 text-purple-400"
        />
        <StatsCard
          title="Total Budget"
          value={`$${((budgetSummary.total_budget || 0) / 1000).toFixed(0)}K`}
          icon={DollarSign}
          color="bg-emerald-500/10 text-emerald-400"
          subtext={`Spent: $${((budgetSummary.total_actual_cost || 0) / 1000).toFixed(1)}K`}
        />
      </div>

      {/* Active Projects */}
      <div className="card-clinical p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Active Projects</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/projects/list")} className="text-slate-400 hover:text-white">
            View All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <div className="space-y-3">
          {stats?.active_projects?.length > 0 ? (
            stats.active_projects.map((project) => (
              <div 
                key={project.id} 
                className="bg-slate-800/50 rounded-lg p-4 hover:bg-slate-800 cursor-pointer transition-colors"
                onClick={() => navigate(`/admin/projects/${project.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white truncate">{project.name}</h3>
                    <p className="text-sm text-slate-400">{project.client_org_name}</p>
                    <p className="text-xs text-slate-500 mt-1">{project.location}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <StatusBadge status={project.status} type="project" />
                    <div className="text-xs text-slate-500">
                      {project.completed_task_count || 0}/{project.task_count || 0} tasks
                    </div>
                  </div>
                </div>
                {project.task_count > 0 && (
                  <Progress 
                    value={(project.completed_task_count / project.task_count) * 100} 
                    className="mt-3 h-1.5"
                  />
                )}
              </div>
            ))
          ) : (
            <p className="text-slate-500 text-center py-8">No active projects</p>
          )}
        </div>
      </div>

      {/* Recent Tasks */}
      <div className="card-clinical p-4">
        <h2 className="text-lg font-semibold text-white mb-4">Recent Task Activity</h2>
        <div className="space-y-2">
          {stats?.recent_tasks?.length > 0 ? (
            stats.recent_tasks.map((task) => {
              const categoryConfig = CATEGORY_CONFIG[task.category] || { icon: ListTodo, label: task.category, color: "text-slate-400 bg-slate-500/10" };
              const CategoryIcon = categoryConfig.icon;
              return (
                <div key={task.id} className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${categoryConfig.color}`}>
                    <CategoryIcon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{task.name}</p>
                    <p className="text-xs text-slate-500">{task.assigned_org_name || "Unassigned"}</p>
                  </div>
                  <StatusBadge status={task.status} type="task" />
                </div>
              );
            })
          ) : (
            <p className="text-slate-500 text-center py-4">No recent tasks</p>
          )}
        </div>
      </div>
    </div>
  );
};

// =====================
// ORGANIZATIONS LIST
// =====================
const OrganizationsList = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    org_type: "hospital_client",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    hipaa_certified: false,
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${API}/projects/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data.organizations);
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/projects/organizations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success("Organization created successfully");
        setShowAddModal(false);
        setFormData({
          name: "",
          org_type: "hospital_client",
          contact_name: "",
          contact_email: "",
          contact_phone: "",
          address: "",
          hipaa_certified: false,
          notes: ""
        });
        fetchOrganizations();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create organization");
      }
    } catch (error) {
      toast.error("Error creating organization");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/projects")} className="text-slate-400">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">Organizations</h1>
            <p className="text-slate-400 text-sm">Manage hospitals, contractors, and vendors</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Organization
        </Button>
      </div>

      {/* Organizations Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {organizations.map((org) => {
          const typeConfig = ORG_TYPE_CONFIG[org.org_type] || { label: org.org_type, color: "text-slate-400 bg-slate-500/10" };
          return (
            <div key={org.id} className="card-clinical p-4 hover:border-slate-600 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeConfig.color}`}>
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{org.name}</h3>
                    <p className="text-xs text-slate-500">{typeConfig.label}</p>
                  </div>
                </div>
                {org.hipaa_certified && (
                  <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-xs rounded-full flex items-center gap-1">
                    <Shield className="w-3 h-3" /> HIPAA
                  </span>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <p className="text-slate-400"><span className="text-slate-500">Contact:</span> {org.contact_name}</p>
                <p className="text-slate-400 truncate"><span className="text-slate-500">Email:</span> {org.contact_email}</p>
                <p className="text-slate-400"><span className="text-slate-500">Phone:</span> {org.contact_phone}</p>
              </div>
            </div>
          );
        })}
      </div>

      {organizations.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No organizations yet</p>
          <Button size="sm" className="mt-4" onClick={() => setShowAddModal(true)}>
            Add First Organization
          </Button>
        </div>
      )}

      {/* Add Organization Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Add Organization</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a new hospital, contractor, or vendor
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Organization Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="e.g., Lyndon B Johnson Hospital"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Organization Type *</Label>
              <Select value={formData.org_type} onValueChange={(v) => setFormData({ ...formData, org_type: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="hospital_client">Hospital Client</SelectItem>
                  <SelectItem value="external_contractor">External Contractor</SelectItem>
                  <SelectItem value="internal_contractor">Internal Contractor</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Contact Name *</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Contact Phone *</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Contact Email *</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="Full address"
              />
            </div>
            <div className="flex items-center gap-3 py-2">
              <Switch
                checked={formData.hipaa_certified}
                onCheckedChange={(checked) => setFormData({ ...formData, hipaa_certified: checked })}
              />
              <Label className="text-slate-300">HIPAA Certified</Label>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowAddModal(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Organization
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =====================
// PROJECTS LIST
// =====================
const ProjectsList = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchProjects();
  }, [filterStatus]);

  const fetchProjects = async () => {
    try {
      let url = `${API}/projects`;
      if (filterStatus !== "all") {
        url += `?status=${filterStatus}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/projects")} className="text-slate-400">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">All Projects</h1>
            <p className="text-slate-400 text-sm">{projects.length} total projects</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="planning">Planning</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => navigate("/admin/projects/new")} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </div>
      </div>

      {/* Projects Table */}
      <div className="card-clinical overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Project</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Client</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Progress</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Budget</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {projects.map((project) => (
                <tr key={project.id} className="hover:bg-slate-800/30">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-white">{project.name}</p>
                      <p className="text-xs text-slate-500">{project.location}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{project.client_org_name}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={project.status} type="project" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={project.task_count > 0 ? (project.completed_task_count / project.task_count) * 100 : 0} 
                        className="w-20 h-2"
                      />
                      <span className="text-xs text-slate-400">
                        {project.completed_task_count}/{project.task_count}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">
                    ${(project.budget || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => navigate(`/admin/projects/${project.id}`)}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {projects.length === 0 && (
        <div className="text-center py-12">
          <FolderKanban className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No projects found</p>
        </div>
      )}
    </div>
  );
};

// =====================
// NEW PROJECT FORM
// =====================
const NewProjectForm = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    client_org_id: "",
    location: "",
    project_type: "RO System Installation",
    estimated_start_date: "",
    estimated_end_date: "",
    budget: "",
    estimate_number: "",
    notes: ""
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${API}/projects/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data.organizations.filter(org => org.org_type === "hospital_client"));
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null
      };
      const response = await fetch(`${API}/projects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });
      if (response.ok) {
        const data = await response.json();
        toast.success("Project created successfully");
        navigate(`/admin/projects/${data.project.id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create project");
      }
    } catch (error) {
      toast.error("Error creating project");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/projects")} className="text-slate-400">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-white">New Project</h1>
          <p className="text-slate-400 text-sm">Create a new water purification project</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card-clinical p-6 space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Project Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="bg-slate-800 border-slate-700 text-white"
              placeholder="e.g., LBJ Hospital RO System Installation"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Client Organization *</Label>
            <Select value={formData.client_org_id} onValueChange={(v) => setFormData({ ...formData, client_org_id: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="Select hospital client" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {organizations.length === 0 && (
              <p className="text-xs text-yellow-400">No hospital clients found. <Link to="/admin/projects/organizations" className="underline">Add one first</Link></p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Location *</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              required
              className="bg-slate-800 border-slate-700 text-white"
              placeholder="e.g., 4th Floor Sterile Processing Department"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Project Type</Label>
            <Select value={formData.project_type} onValueChange={(v) => setFormData({ ...formData, project_type: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="RO System Installation">RO System Installation</SelectItem>
                <SelectItem value="Plumbing Pre-work">Plumbing Pre-work</SelectItem>
                <SelectItem value="Electrical Pre-work">Electrical Pre-work</SelectItem>
                <SelectItem value="Full Installation">Full Installation (Plumbing + Electrical + RO)</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
                <SelectItem value="Upgrade">System Upgrade</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Description *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              className="bg-slate-800 border-slate-700 text-white"
              rows={4}
              placeholder="Describe the project scope and objectives..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Estimated Start Date</Label>
              <Input
                type="date"
                value={formData.estimated_start_date}
                onChange={(e) => setFormData({ ...formData, estimated_start_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Estimated End Date</Label>
              <Input
                type="date"
                value={formData.estimated_end_date}
                onChange={(e) => setFormData({ ...formData, estimated_end_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Budget ($)</Label>
              <Input
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="e.g., 76000"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Estimate Number</Label>
              <Input
                value={formData.estimate_number}
                onChange={(e) => setFormData({ ...formData, estimate_number: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="e.g., 1139"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
              rows={3}
              placeholder="Additional notes..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={() => navigate("/admin/projects")} className="text-slate-400">
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || !formData.client_org_id} className="bg-blue-600 hover:bg-blue-700">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Project
          </Button>
        </div>
      </form>
    </div>
  );
};

// =====================
// PROJECT DETAIL VIEW
// =====================
const ProjectDetail = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [costs, setCosts] = useState([]);
  const [organizations, setOrganizations] = useState({});
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tasks");
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showAddCostModal, setShowAddCostModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showEntityDetailModal, setShowEntityDetailModal] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [stakeholderTable, setStakeholderTable] = useState(null);
  const [financials, setFinancials] = useState(null);
  
  // Share & Notify state
  const [showShareModal, setShowShareModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState("");

  const isAdmin = user?.role === "super_admin" || user?.role === "admin";

  useEffect(() => {
    fetchProjectData();
    fetchAllOrganizations();
  }, [projectId]);

  // Fetch stakeholder table when tab is selected
  useEffect(() => {
    if (activeTab === "stakeholder" && projectId && !stakeholderTable) {
      fetchStakeholderTable();
    }
    if (activeTab === "financials" && projectId && !financials) {
      fetchFinancials();
    }
  }, [activeTab, projectId]);

  const fetchStakeholderTable = async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/stakeholder-table`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStakeholderTable(data);
      }
    } catch (error) {
      console.error("Error fetching stakeholder table:", error);
    }
  };

  const fetchFinancials = async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/financials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFinancials(data);
      }
    } catch (error) {
      console.error("Error fetching financials:", error);
    }
  };

  const fetchProjectData = async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // API returns project data directly with tasks and costs embedded
        setProject(data);
        setTasks(data.tasks || []);
        setCosts(data.costs || []);
        // Organizations are fetched separately
      }
    } catch (error) {
      console.error("Error fetching project:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllOrganizations = async () => {
    try {
      const response = await fetch(`${API}/projects/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllOrganizations(data.organizations);
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const updateProjectStatus = async (newStatus) => {
    try {
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success("Project status updated");
        fetchProjectData();
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Create shareable link for project snapshot
  const handleCreateShareLink = async () => {
    setShareLoading(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/share`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ expires_hours: 168 }) // 7 days
      });
      if (response.ok) {
        const data = await response.json();
        setShareLink(data);
        toast.success("Share link created!");
      } else {
        toast.error("Failed to create share link");
      }
    } catch (error) {
      toast.error("Failed to create share link");
    } finally {
      setShareLoading(false);
    }
  };

  // Copy share link to clipboard
  const handleCopyShareLink = () => {
    if (shareLink?.share_url) {
      navigator.clipboard.writeText(shareLink.share_url);
      toast.success("Link copied to clipboard!");
    }
  };

  // Send notification to stakeholders
  const handleSendNotification = async () => {
    if (!notifyMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }
    setNotifyLoading(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/send-notification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          message: notifyMessage,
          notify_email: true,
          notify_telegram: true,
          include_progress: true
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Notification sent! (${data.emails_sent} emails, ${data.telegrams_sent} Telegram)`);
        setShowNotifyModal(false);
        setNotifyMessage("");
      } else {
        toast.error("Failed to send notification");
      }
    } catch (error) {
      toast.error("Failed to send notification");
    } finally {
      setNotifyLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Project not found</p>
        <Button className="mt-4" onClick={() => navigate("/admin/projects")}>
          Back to Projects
        </Button>
      </div>
    );
  }

  const progressPercent = project.task_count > 0 
    ? Math.round((project.completed_task_count / project.task_count) * 100) 
    : 0;

  // Group tasks by category
  const tasksByCategory = tasks.reduce((acc, task) => {
    const cat = task.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(task);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/projects")} className="text-slate-500">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{project.name}</h1>
            <p className="text-slate-500 text-sm">{project.client_org_name} • {project.location}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={project.status} onValueChange={updateProjectStatus}>
            <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="planning">Planning</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-purple-700 text-purple-400 hover:bg-purple-900/30"
                onClick={() => setShowShareModal(true)}
                data-testid="project-share-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-amber-700 text-amber-400 hover:bg-amber-900/30"
                onClick={() => setShowNotifyModal(true)}
                data-testid="project-notify-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                Notify
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/30"
                onClick={() => setShowEntityDetailModal(true)}
                data-testid="project-po-scope-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                PO & Work Scope
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-blue-700 text-blue-400 hover:bg-blue-900/30"
                onClick={() => setShowSendEmailModal(true)}
                data-testid="project-send-email-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Update
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" className="border-slate-700 text-slate-300" onClick={() => navigate(`/admin/projects/${projectId}/sow`)}>
            <FileText className="w-4 h-4 mr-2" />
            Generate SOW
          </Button>
        </div>
      </div>

      {/* Project Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Progress"
          value={`${progressPercent}%`}
          icon={BarChart3}
          color="bg-blue-500/10 text-blue-400"
          subtext={`${project.completed_task_count}/${project.task_count} tasks`}
        />
        <StatsCard
          title="Budget"
          value={`$${(project.budget || 0).toLocaleString()}`}
          icon={DollarSign}
          color="bg-emerald-500/10 text-emerald-400"
          subtext={`Spent: $${(project.actual_cost || 0).toLocaleString()}`}
        />
        <StatsCard
          title="Est. Start"
          value={project.estimated_start_date ? new Date(project.estimated_start_date).toLocaleDateString() : "TBD"}
          icon={Calendar}
          color="bg-purple-500/10 text-purple-400"
        />
        <StatsCard
          title="Est. End"
          value={project.estimated_end_date ? new Date(project.estimated_end_date).toLocaleDateString() : "TBD"}
          icon={Calendar}
          color="bg-orange-500/10 text-orange-400"
        />
      </div>

      {/* Progress Bar */}
      <div className="card-clinical p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-500">Overall Progress</span>
          <span className="text-sm font-medium text-slate-900">{progressPercent}%</span>
        </div>
        <Progress value={progressPercent} className="h-3" />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-4 overflow-x-auto scrollbar-hide">
          {["tasks", "procurement", "standards", "stakeholder", "financials", "changeorders", "payapps", "submittals", "rfis", "fieldlogs", "icra", "updates", "activity", "files", "team", "costs", "timeline", "documents"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? "text-blue-600 border-blue-600"
                  : "text-slate-500 border-transparent hover:text-slate-900"
              }`}
              data-testid={`project-tab-${tab}`}
            >
              {tab === "updates" ? "Status Updates" :
               tab === "procurement" ? "Procurement & POs" : 
               tab === "standards" ? "Standards & Compliance" :
               tab === "stakeholder" ? "Stakeholder Table" :
               tab === "financials" ? "Financial Summary" :
               tab === "changeorders" ? "Change Orders" :
               tab === "payapps" ? "Pay Applications" :
               tab === "rfis" ? "RFIs" :
               tab === "fieldlogs" ? "Daily Logs" :
               tab === "icra" ? "ICRA Permits" :
               tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "procurement" && (
        <ProjectProcurementTab projectId={projectId} project={project} token={token} />
      )}

      {activeTab === "standards" && (
        <StandardsCompass linkType="project" linkId={projectId} token={token}
          label={project?.name || "Project"} />
      )}

      {activeTab === "changeorders" && (
        <ChangeOrdersTab
          projectId={projectId}
          project={project}
          token={token}
          onContractChange={() => { fetchProjectData(); fetchFinancials(); }}
        />
      )}

      {activeTab === "payapps" && (
        <PayApplicationsTab projectId={projectId} token={token} />
      )}

      {activeTab === "submittals" && (
        <SubmittalsTab projectId={projectId} project={project} token={token} />
      )}

      {activeTab === "rfis" && (
        <RfisTab
          projectId={projectId}
          project={project}
          token={token}
          onContractChange={() => { fetchProjectData(); fetchFinancials(); }}
        />
      )}

      {activeTab === "fieldlogs" && (
        <FieldLogsTab projectId={projectId} project={project} token={token} />
      )}

      {activeTab === "icra" && (
        <IcraPermitsTab projectId={projectId} project={project} token={token} />
      )}

      {/* Tab Content */}
      {activeTab === "tasks" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">Project Tasks</h2>
            <Button size="sm" onClick={() => setShowAddTaskModal(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </Button>
          </div>

          {/* Tasks by Category */}
          {Object.entries(tasksByCategory).length > 0 ? (
            Object.entries(tasksByCategory).map(([category, categoryTasks]) => {
              const config = CATEGORY_CONFIG[category] || { icon: ListTodo, label: category, color: "text-slate-400 bg-slate-500/10" };
              const CategoryIcon = config.icon;
              return (
                <div key={category} className="card-clinical p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.color}`}>
                      <CategoryIcon className="w-4 h-4" />
                    </div>
                    <h3 className="font-medium text-white">{config.label}</h3>
                    <span className="text-xs text-slate-500">({categoryTasks.length} tasks)</span>
                  </div>
                  <div className="space-y-2">
                    {categoryTasks.map((task) => (
                      <TaskRow 
                        key={task.id} 
                        task={task} 
                        onEdit={() => setEditingTask(task)}
                        onStatusChange={() => fetchProjectData()}
                        token={token}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 card-clinical">
              <ListTodo className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No tasks yet</p>
              <Button size="sm" className="mt-4" onClick={() => setShowAddTaskModal(true)}>
                Add First Task
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Stakeholder Table Tab */}
      {activeTab === "stakeholder" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-white">Live Stakeholder Table</h2>
              <p className="text-sm text-slate-400">Executive reporting view - synchronized from field execution</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => fetchStakeholderTable()}>
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button size="sm" variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </div>

          {stakeholderTable ? (
            <>
              {/* Stakeholder Summary */}
              {stakeholderTable.stakeholders && (
                <div className="flex flex-wrap gap-4 p-4 bg-slate-800/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-slate-300">Client: {stakeholderTable.client || 'N/A'}</span>
                  </div>
                  {stakeholderTable.stakeholders.contractors?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <HardHat className="w-4 h-4 text-amber-400" />
                      <span className="text-sm text-slate-300">Contractors: {stakeholderTable.stakeholders.contractors.join(', ')}</span>
                    </div>
                  )}
                  {stakeholderTable.stakeholders.suppliers?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-slate-300">Suppliers: {stakeholderTable.stakeholders.suppliers.join(', ')}</span>
                    </div>
                  )}
                </div>
              )}

              {/* The Table */}
              <div className="card-clinical overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-800/80">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Phase / Task</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Owner</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Due Date</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Status & Scope of Work</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {stakeholderTable.table?.map((row, idx) => (
                      row.type === "phase_header" ? (
                        <tr key={idx} className="bg-slate-700/30">
                          <td colSpan={4} className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <span className={`w-2 h-2 rounded-full ${
                                row.status === 'completed' ? 'bg-green-500' : 
                                row.status === 'in_progress' ? 'bg-blue-500' : 'bg-slate-500'
                              }`} />
                              <span className="font-semibold text-white">{row.phase}</span>
                              <span className="text-xs text-slate-400">({row.progress})</span>
                              {row.status === 'completed' && (
                                <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded">Complete</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr key={idx} className="hover:bg-slate-800/30">
                          <td className="px-4 py-3 pl-8">
                            <span className="text-slate-300">{row.task}</span>
                          </td>
                          <td className="px-4 py-3 text-slate-400">{row.owner}</td>
                          <td className="px-4 py-3 text-slate-400">{row.due_date || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded w-fit ${
                                row.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                                row.status === 'in_progress' ? 'bg-blue-500/20 text-blue-300' :
                                row.status === 'blocked' ? 'bg-red-500/20 text-red-300' :
                                'bg-slate-500/20 text-slate-300'
                              }`}>
                                {row.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                                {row.status === 'in_progress' && <Clock className="w-3 h-3" />}
                                {row.status === 'blocked' && <AlertTriangle className="w-3 h-3" />}
                                {row.status?.replace('_', ' ')}
                              </span>
                              {row.scope_of_work && (
                                <span className="text-xs text-slate-500 line-clamp-1">{row.scope_of_work}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Financial Summary in Table */}
              {stakeholderTable.financials && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/20 border border-emerald-500/30 rounded-lg p-4">
                    <p className="text-emerald-400 text-sm">Customer Price</p>
                    <p className="text-2xl font-bold text-white">${stakeholderTable.financials.customer_price?.toLocaleString()}</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-blue-400 text-sm">Contractor Payout</p>
                    <p className="text-2xl font-bold text-white">${stakeholderTable.financials.contractor_payout?.toLocaleString()}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-500/30 rounded-lg p-4">
                    <p className="text-purple-400 text-sm">SterileMate Revenue</p>
                    <p className="text-2xl font-bold text-white">${stakeholderTable.financials.sterilemate_revenue?.toLocaleString()}</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 border border-amber-500/30 rounded-lg p-4">
                    <p className="text-amber-400 text-sm">Margin</p>
                    <p className="text-2xl font-bold text-white">{stakeholderTable.financials.margin_percentage}%</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
              Loading stakeholder table...
            </div>
          )}
        </div>
      )}

      {/* Financial Summary Tab */}
      {activeTab === "financials" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-white">Financial Summary</h2>
              <p className="text-sm text-slate-400">SterileMate 44% management fee overlay</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => fetchFinancials()}>
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {financials ? (
            <>
              {/* Main Financial Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/20 border border-emerald-500/30 rounded-xl p-5">
                  <p className="text-emerald-400 text-sm mb-1">Customer Quoted Price</p>
                  <p className="text-3xl font-bold text-white">${financials.customer_quoted_price?.toLocaleString() || financials.budget?.toLocaleString() || '0'}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-500/30 rounded-xl p-5">
                  <p className="text-blue-400 text-sm mb-1">Contractor Payout</p>
                  <p className="text-3xl font-bold text-white">${financials.financials?.contractor_payout?.toLocaleString() || '0'}</p>
                  <p className="text-xs text-blue-300 mt-1">Price ÷ 1.44</p>
                </div>
                <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-500/30 rounded-xl p-5">
                  <p className="text-purple-400 text-sm mb-1">SterileMate Revenue</p>
                  <p className="text-3xl font-bold text-white">${financials.financials?.sterilemate_revenue?.toLocaleString() || '0'}</p>
                  <p className="text-xs text-purple-300 mt-1">44% Management Fee</p>
                </div>
                <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 border border-amber-500/30 rounded-xl p-5">
                  <p className="text-amber-400 text-sm mb-1">Margin</p>
                  <p className="text-3xl font-bold text-white">{financials.financials?.margin_percentage || '0'}%</p>
                </div>
              </div>

              {/* Cost vs Budget */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="card-clinical p-5">
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    Budget vs Actual
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Estimated Cost</span>
                      <span className="text-white font-medium">${financials.total_estimated_cost?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Actual Cost</span>
                      <span className="text-white font-medium">${financials.total_actual_cost?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-700 pt-3">
                      <span className="text-slate-400">Remaining Budget</span>
                      <span className={`font-medium ${(financials.financials?.profit_vs_budget || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${financials.financials?.profit_vs_budget?.toLocaleString() || '0'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card-clinical p-5">
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    Cost Breakdown
                  </h3>
                  <div className="space-y-2">
                    {financials.cost_breakdown && Object.entries(financials.cost_breakdown).map(([cat, amount]) => (
                      <div key={cat} className="flex justify-between">
                        <span className="text-slate-400 capitalize">{cat}</span>
                        <span className="text-white">${(amount || 0).toLocaleString()}</span>
                      </div>
                    ))}
                    {(!financials.cost_breakdown || Object.keys(financials.cost_breakdown).length === 0) && (
                      <p className="text-slate-500 text-sm">No cost breakdown available</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Task Progress */}
              <div className="card-clinical p-5">
                <h3 className="font-semibold text-white mb-4">Task Completion</h3>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all"
                        style={{ width: `${financials.task_count > 0 ? (financials.completed_tasks / financials.task_count * 100) : 0}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-white font-medium">
                    {financials.completed_tasks} / {financials.task_count} tasks
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
              Loading financial data...
            </div>
          )}
        </div>
      )}

      {activeTab === "costs" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">Cost Tracking</h2>
            <Button size="sm" onClick={() => setShowAddCostModal(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Cost
            </Button>
          </div>

          {/* Cost Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {["labor", "materials", "travel", "subcontractor"].map((cat) => {
              const total = costs.filter(c => c.category === cat).reduce((sum, c) => sum + c.amount, 0);
              return (
                <div key={cat} className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 capitalize">{cat}</p>
                  <p className="text-xl font-bold text-white">${total.toLocaleString()}</p>
                </div>
              );
            })}
          </div>

          {/* Cost List */}
          <div className="card-clinical overflow-hidden">
            {costs.length > 0 ? (
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Date</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Category</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Description</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-slate-400">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {costs.map((cost) => (
                    <tr key={cost.id}>
                      <td className="px-4 py-3 text-slate-300">{new Date(cost.date).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-slate-300 capitalize">{cost.category}</td>
                      <td className="px-4 py-3 text-slate-300">{cost.description}</td>
                      <td className="px-4 py-3 text-right text-white font-medium">${cost.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No costs recorded yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "timeline" && (
        <div className="card-clinical p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Project Timeline</h2>
          <div className="relative">
            {tasks.length > 0 ? (
              <div className="space-y-4">
                {tasks.map((task, idx) => {
                  const config = CATEGORY_CONFIG[task.category] || { icon: ListTodo, label: task.category, color: "text-slate-400 bg-slate-500/10" };
                  const CategoryIcon = config.icon;
                  return (
                    <div key={task.id} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          task.status === "completed" ? "bg-green-500/20 text-green-400" :
                          task.status === "in_progress" ? "bg-blue-500/20 text-blue-400" :
                          "bg-slate-700 text-slate-400"
                        }`}>
                          {task.status === "completed" ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <CategoryIcon className="w-5 h-5" />
                          )}
                        </div>
                        {idx < tasks.length - 1 && (
                          <div className="w-0.5 h-full bg-slate-700 mt-2" />
                        )}
                      </div>
                      <div className="flex-1 pb-8">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-white">{task.name}</h4>
                            <p className="text-sm text-slate-400">{task.assigned_org_name || "Unassigned"}</p>
                          </div>
                          <StatusBadge status={task.status} type="task" />
                        </div>
                        {task.estimated_start_date && (
                          <p className="text-xs text-slate-500 mt-2">
                            {task.estimated_start_date} → {task.estimated_end_date || "TBD"}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No tasks to display in timeline</p>
            )}
          </div>
        </div>
      )}

      {activeTab === "updates" && (
        <div className="card-clinical p-6">
          <ProjectStatusUpdates projectId={projectId} projectName={project.name} />
        </div>
      )}

      {activeTab === "activity" && (
        <ActivityFeed projectId={projectId} token={token} />
      )}

      {activeTab === "files" && (
        <FilesTab projectId={projectId} tasks={tasks} token={token} />
      )}

      {activeTab === "team" && (
        <TeamTab 
          projectId={projectId} 
          project={project}
          organizations={allOrganizations}
          token={token}
          onRefresh={fetchProjectData}
        />
      )}

      {activeTab === "documents" && (
        <div className="card-clinical p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Project Documents</h2>
          <div className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-start border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={() => navigate(`/admin/projects/${projectId}/sow`)}
            >
              <FileText className="w-5 h-5 mr-3 text-blue-400" />
              <span>Statement of Work (SOW)</span>
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start border-slate-700 text-slate-300 hover:bg-slate-800"
              disabled
            >
              <ScrollText className="w-5 h-5 mr-3 text-purple-400" />
              <span>Project Proposal</span>
              <span className="ml-auto text-xs text-slate-500">Coming Soon</span>
            </Button>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      <AddTaskModal
        open={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        projectId={projectId}
        organizations={allOrganizations}
        token={token}
        onSuccess={() => {
          fetchProjectData();
          setShowAddTaskModal(false);
        }}
      />

      {/* Edit Task Modal */}
      {editingTask && (
        <EditTaskModal
          open={!!editingTask}
          onClose={() => setEditingTask(null)}
          task={editingTask}
          organizations={allOrganizations}
          token={token}
          onSuccess={() => {
            fetchProjectData();
            setEditingTask(null);
          }}
        />
      )}

      {/* Add Cost Modal */}
      <AddCostModal
        open={showAddCostModal}
        onClose={() => setShowAddCostModal(false)}
        projectId={projectId}
        tasks={tasks}
        token={token}
        onSuccess={() => {
          fetchProjectData();
          setShowAddCostModal(false);
        }}
      />

      {/* Entity Detail Modal (PO & Work Scope) */}
      <EntityDetailModal
        open={showEntityDetailModal}
        onClose={() => setShowEntityDetailModal(false)}
        entityType="project"
        entityId={projectId}
      />
      {/* Send Update Email Modal */}
      <SendUpdateEmailModal
        open={showSendEmailModal}
        onClose={() => setShowSendEmailModal(false)}
        entityType="project"
        entityId={projectId}
        entityName={project?.name}
      />
      
      {/* Share Snapshot Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-purple-500" />
              Share Project Snapshot
            </DialogTitle>
            <DialogDescription>
              Generate a secure link to share a read-only view of this project with stakeholders.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 rounded-lg p-4 border">
              <p className="text-sm font-medium text-slate-700">Project: {project?.name}</p>
              <p className="text-xs text-slate-500 mt-1">Link expires in 7 days</p>
            </div>
            
            {shareLink ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm text-emerald-600 font-medium">Share link created!</span>
                </div>
                <div className="p-3 bg-slate-100 rounded-lg border">
                  <p className="text-xs text-slate-500 mb-1">Share URL:</p>
                  <p className="text-sm font-mono text-slate-700 break-all">{shareLink.share_url}</p>
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1" variant="outline" onClick={handleCopyShareLink}>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button className="flex-1" onClick={() => window.open(shareLink.share_url, '_blank')}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Link
                  </Button>
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Expires: {new Date(shareLink.expires_at).toLocaleDateString()} at {new Date(shareLink.expires_at).toLocaleTimeString()}
                </p>
              </div>
            ) : (
              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700" 
                onClick={handleCreateShareLink}
                disabled={shareLoading}
              >
                {shareLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Generate Share Link
                  </>
                )}
              </Button>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowShareModal(false); setShareLink(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Notify Stakeholders Modal */}
      <Dialog open={showNotifyModal} onOpenChange={setShowNotifyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Notify Stakeholders
            </DialogTitle>
            <DialogDescription>
              Send a notification to all project stakeholders via Email and Telegram.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 rounded-lg p-4 border">
              <p className="text-sm font-medium text-slate-700">Project: {project?.name}</p>
              <p className="text-xs text-slate-500 mt-1">Notification will be sent to all stakeholders</p>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Message</Label>
              <Textarea
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                placeholder="Enter your update message..."
                rows={4}
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>Email</span>
              </div>
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                <span>Telegram</span>
              </div>
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                <span>Include Progress</span>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowNotifyModal(false); setNotifyMessage(""); }}>
              Cancel
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700" 
              onClick={handleSendNotification}
              disabled={notifyLoading || !notifyMessage.trim()}
            >
              {notifyLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Send Notification
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Task Row Component
const TaskRow = ({ task, onEdit, onStatusChange, token }) => {
  const [updating, setUpdating] = useState(false);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await fetch(`${API}/projects/tasks/${task.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success("Task updated");
        onStatusChange();
      }
    } catch (error) {
      toast.error("Failed to update task");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg hover:bg-slate-800/50">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-white truncate">{task.name}</p>
          <PriorityBadge priority={task.priority} />
        </div>
        <p className="text-sm text-slate-500">
          {task.assigned_org_name || "Unassigned"}
          {task.estimated_start_date && ` • ${task.estimated_start_date}`}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Select value={task.status} onValueChange={updateStatus} disabled={updating}>
          <SelectTrigger className="w-32 h-8 bg-slate-800 border-slate-700 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" onClick={onEdit} className="text-slate-400 hover:text-white">
          <Edit className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Add Task Modal
const AddTaskModal = ({ open, onClose, projectId, organizations, token, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "plumbing_prework",
    assigned_org_id: "",
    estimated_start_date: "",
    estimated_end_date: "",
    estimated_cost: "",
    priority: "normal"
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        project_id: projectId,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        assigned_org_id: formData.assigned_org_id === "unassigned" ? null : formData.assigned_org_id || null
      };
      const response = await fetch(`${API}/projects/${projectId}/tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });
      if (response.ok) {
        toast.success("Task created");
        setFormData({
          name: "",
          description: "",
          category: "plumbing_prework",
          assigned_org_id: "",
          estimated_start_date: "",
          estimated_end_date: "",
          estimated_cost: "",
          priority: "normal"
        });
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create task");
      }
    } catch (error) {
      toast.error("Error creating task");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Add Task</DialogTitle>
          <DialogDescription className="text-slate-400">
            Add a new task to this project
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Task Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="bg-slate-800 border-slate-700 text-white"
              placeholder="e.g., Install PVC Schedule 80 piping"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Assigned Organization</Label>
            <Select value={formData.assigned_org_id} onValueChange={(v) => setFormData({ ...formData, assigned_org_id: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Est. Start</Label>
              <Input
                type="date"
                value={formData.estimated_start_date}
                onChange={(e) => setFormData({ ...formData, estimated_start_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Est. End</Label>
              <Input
                type="date"
                value={formData.estimated_end_date}
                onChange={(e) => setFormData({ ...formData, estimated_end_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Estimated Cost ($)</Label>
            <Input
              type="number"
              value={formData.estimated_cost}
              onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
              placeholder="e.g., 42000"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-400">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Task
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit Task Modal
const EditTaskModal = ({ open, onClose, task, organizations, token, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: task.name,
    description: task.description || "",
    category: task.category,
    assigned_org_id: task.assigned_org_id || "",
    estimated_start_date: task.estimated_start_date || "",
    estimated_end_date: task.estimated_end_date || "",
    actual_start_date: task.actual_start_date || "",
    actual_end_date: task.actual_end_date || "",
    estimated_cost: task.estimated_cost || "",
    actual_cost: task.actual_cost || "",
    priority: task.priority,
    completion_notes: task.completion_notes || ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        actual_cost: formData.actual_cost ? parseFloat(formData.actual_cost) : null,
        assigned_org_id: formData.assigned_org_id === "unassigned" ? null : formData.assigned_org_id || null
      };
      const response = await fetch(`${API}/projects/tasks/${task.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });
      if (response.ok) {
        toast.success("Task updated");
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update task");
      }
    } catch (error) {
      toast.error("Error updating task");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Edit Task</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Task Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Assigned Organization</Label>
            <Select value={formData.assigned_org_id} onValueChange={(v) => setFormData({ ...formData, assigned_org_id: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Actual Start</Label>
              <Input
                type="date"
                value={formData.actual_start_date}
                onChange={(e) => setFormData({ ...formData, actual_start_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Actual End</Label>
              <Input
                type="date"
                value={formData.actual_end_date}
                onChange={(e) => setFormData({ ...formData, actual_end_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Est. Cost ($)</Label>
              <Input
                type="number"
                value={formData.estimated_cost}
                onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Actual Cost ($)</Label>
              <Input
                type="number"
                value={formData.actual_cost}
                onChange={(e) => setFormData({ ...formData, actual_cost: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Completion Notes</Label>
            <Textarea
              value={formData.completion_notes}
              onChange={(e) => setFormData({ ...formData, completion_notes: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
              rows={3}
              placeholder="Notes about task completion, issues, etc."
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-400">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add Cost Modal
const AddCostModal = ({ open, onClose, projectId, tasks, token, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    category: "labor",
    description: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    task_id: "",
    vendor: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        project_id: projectId,
        amount: parseFloat(formData.amount),
        task_id: formData.task_id === "none" ? null : formData.task_id || null
      };
      const response = await fetch(`${API}/projects/${projectId}/costs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });
      if (response.ok) {
        toast.success("Cost entry added");
        setFormData({
          category: "labor",
          description: "",
          amount: "",
          date: new Date().toISOString().split("T")[0],
          task_id: "",
          vendor: ""
        });
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add cost");
      }
    } catch (error) {
      toast.error("Error adding cost");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">Add Cost Entry</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="labor">Labor</SelectItem>
                  <SelectItem value="materials">Materials</SelectItem>
                  <SelectItem value="travel">Travel</SelectItem>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Link to Task</Label>
            <Select value={formData.task_id} onValueChange={(v) => setFormData({ ...formData, task_id: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="Optional - select task" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="none">No task</SelectItem>
                {tasks.map((task) => (
                  <SelectItem key={task.id} value={task.id}>{task.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Description *</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              className="bg-slate-800 border-slate-700 text-white"
              placeholder="e.g., Electrical conduit installation"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Amount ($) *</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="e.g., 5000"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Vendor</Label>
              <Input
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="e.g., Vaughn Construction"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-400">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Cost
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// =====================
// SOW DOCUMENT PAGE
// =====================
const SOWDocumentPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [sowData, setSOWData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSOWData();
  }, [projectId]);

  const fetchSOWData = async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/sow`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSOWData(data);
      }
    } catch (error) {
      console.error("Error fetching SOW data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!sowData) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Failed to load SOW data</p>
        <Button className="mt-4" onClick={() => navigate(`/admin/projects/${projectId}`)}>
          Back to Project
        </Button>
      </div>
    );
  }

  const { project, client_organization, tasks_by_category, costs_by_category } = sowData;

  return (
    <div className="space-y-6">
      {/* Header (Hidden in print) */}
      <div className="print:hidden flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/projects/${projectId}`)} className="text-slate-400">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">Statement of Work</h1>
            <p className="text-slate-400 text-sm">{project.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={async () => {
              try {
                const token = localStorage.getItem('admin_token');
                const response = await fetch(`${API}/projects/${projectId}/sow/download`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('Download failed');
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `SOW_${project.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (err) {
                console.error('Download error:', err);
                alert('Failed to download SOW document');
              }
            }} 
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
            data-testid="download-sow-word"
          >
            <FileText className="w-4 h-4 mr-2" />
            Download Word
          </Button>
          <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
            <Download className="w-4 h-4 mr-2" />
            Download / Print
          </Button>
        </div>
      </div>

      {/* SOW Document */}
      <div className="bg-white text-slate-900 rounded-lg p-8 print:p-0 print:rounded-none">
        {/* Header */}
        <div className="border-b-2 border-blue-600 pb-6 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-blue-900">STATEMENT OF WORK</h1>
              <p className="text-slate-600 mt-1">{project.project_type}</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-blue-900">Sterile Mate LLC</p>
              <p className="text-sm text-slate-600">396 W Greens Road, Suite 1139</p>
              <p className="text-sm text-slate-600">Houston, TX 77067</p>
              <p className="text-sm text-slate-600">800.988.3892</p>
            </div>
          </div>
        </div>

        {/* Project Info */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h2 className="text-lg font-bold text-blue-900 mb-3">Project Information</h2>
            <table className="text-sm">
              <tbody>
                <tr><td className="pr-4 py-1 text-slate-600">Project Name:</td><td className="font-medium">{project.name}</td></tr>
                <tr><td className="pr-4 py-1 text-slate-600">Location:</td><td>{project.location}</td></tr>
                <tr><td className="pr-4 py-1 text-slate-600">Estimate #:</td><td>{project.estimate_number || "N/A"}</td></tr>
                <tr><td className="pr-4 py-1 text-slate-600">Date:</td><td>{new Date().toLocaleDateString()}</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <h2 className="text-lg font-bold text-blue-900 mb-3">Client Information</h2>
            {client_organization && (
              <table className="text-sm">
                <tbody>
                  <tr><td className="pr-4 py-1 text-slate-600">Organization:</td><td className="font-medium">{client_organization.name}</td></tr>
                  <tr><td className="pr-4 py-1 text-slate-600">Contact:</td><td>{client_organization.contact_name}</td></tr>
                  <tr><td className="pr-4 py-1 text-slate-600">Email:</td><td>{client_organization.contact_email}</td></tr>
                  <tr><td className="pr-4 py-1 text-slate-600">Phone:</td><td>{client_organization.contact_phone}</td></tr>
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Project Overview */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-blue-900 mb-3">Project Overview</h2>
          <p className="text-slate-700">{project.description}</p>
        </div>

        {/* Scope of Work */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-blue-900 mb-4">Scope of Work</h2>
          {Object.entries(tasks_by_category).map(([category, categoryTasks]) => {
            const config = CATEGORY_CONFIG[category] || { label: category };
            return (
              <div key={category} className="mb-6">
                <h3 className="font-bold text-slate-800 mb-2 border-b pb-1">{config.label}</h3>
                <ul className="list-disc pl-6 space-y-1">
                  {categoryTasks.map((task) => (
                    <li key={task.id} className="text-slate-700">
                      <span className="font-medium">{task.name}</span>
                      {task.description && <span className="text-slate-600"> - {task.description}</span>}
                      {task.assigned_org_name && <span className="text-slate-500 text-sm"> (Owner: {task.assigned_org_name})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Timeline */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-blue-900 mb-3">Project Timeline</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Estimated Start Date</th>
                <th className="text-left py-2">Estimated Completion Date</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2">{project.estimated_start_date || "TBD"}</td>
                <td className="py-2">{project.estimated_end_date || "TBD"}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Budget */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-blue-900 mb-3">Budget Summary</h2>
          <div className="bg-slate-100 rounded-lg p-4">
            <div className="flex justify-between items-center text-lg">
              <span className="font-medium">Total Project Budget:</span>
              <span className="font-bold text-blue-900">${(project.budget || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Compliance */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-blue-900 mb-3">Compliance & Standards</h2>
          <p className="text-slate-700">
            All work will be performed in compliance with AAMI ST108 standards, hospital safety policies, 
            and infection control protocols. HIPAA guidelines will be followed for all project communications 
            and documentation.
          </p>
        </div>

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-8 mt-12 pt-8 border-t">
          <div>
            <p className="font-bold mb-8">Contractor:</p>
            <div className="border-b border-slate-400 mb-2"></div>
            <p className="text-sm text-slate-600">George Broughton III, CEO</p>
            <p className="text-sm text-slate-600">Sterile Mate LLC</p>
            <p className="text-sm text-slate-600 mt-2">Date: ________________</p>
          </div>
          <div>
            <p className="font-bold mb-8">Client Approval:</p>
            <div className="border-b border-slate-400 mb-2"></div>
            <p className="text-sm text-slate-600">{client_organization?.contact_name || "Authorized Representative"}</p>
            <p className="text-sm text-slate-600">{client_organization?.name}</p>
            <p className="text-sm text-slate-600 mt-2">Date: ________________</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-4 border-t text-center text-xs text-slate-500">
          <p>Sterile Mate LLC | 396 W Greens Road, Suite 1139, Houston, TX 77067 | 800.988.3892 | info@sterilemate.com</p>
          <p className="mt-1">This document is confidential and intended for authorized parties only.</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .bg-white, .bg-white * {
            visibility: visible;
          }
          .bg-white {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

// =====================
// ACTIVITY FEED COMPONENT
// =====================
const ActivityFeed = ({ projectId, token }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, [projectId]);

  const fetchActivities = async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities);
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "comment": return MessageSquare;
      case "handoff": return ArrowRightLeft;
      case "file_upload": return Upload;
      case "status_change": return CheckCircle;
      case "assignment": return Users;
      case "created": return Plus;
      default: return Activity;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="card-clinical p-4">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5 text-blue-400" />
        Activity Feed
      </h2>
      
      {activities.length > 0 ? (
        <div className="space-y-3">
          {activities.map((activity) => {
            const Icon = getActivityIcon(activity.type);
            return (
              <div key={activity.id} className="flex gap-3 p-3 bg-slate-800/30 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white">{activity.content}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {activity.created_by_name} • {new Date(activity.created_at).toLocaleString()}
                  </p>
                  {activity.details?.preview && (
                    <p className="text-xs text-slate-400 mt-2 italic">&quot;{activity.details.preview}&quot;</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <Activity className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No activity yet</p>
          <p className="text-xs text-slate-500 mt-1">Activity will appear as team members work on tasks</p>
        </div>
      )}
    </div>
  );
};

// =====================
// FILES TAB COMPONENT
// =====================
const FilesTab = ({ projectId, tasks, token }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedTaskId, setSelectedTaskId] = useState("");

  useEffect(() => {
    fetchFiles();
  }, [projectId]);

  const fetchFiles = async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFiles(data.files);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    if (selectedTaskId && selectedTaskId !== "none") {
      formData.append("task_id", selectedTaskId);
    }

    try {
      const response = await fetch(`${API}/projects/${projectId}/files`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        toast.success("File uploaded successfully");
        fetchFiles();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload file");
      }
    } catch (error) {
      toast.error("Error uploading file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await fetch(`${API}/files/${file.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.original_name;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error("Error downloading file");
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith("image/")) return Image;
    if (mimeType?.includes("pdf")) return FileText;
    if (mimeType?.includes("spreadsheet") || mimeType?.includes("excel")) return FileSpreadsheet;
    if (mimeType?.includes("presentation") || mimeType?.includes("powerpoint")) return Presentation;
    if (mimeType?.includes("zip") || mimeType?.includes("archive")) return FileArchive;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Upload Section */}
      <div className="card-clinical p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-400" />
            Project Files
          </h2>
        </div>
        
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <Label className="text-slate-400 text-xs mb-1 block">Link to Task (Optional)</Label>
            <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="Select task..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="none">No task</SelectItem>
                {tasks.map((task) => (
                  <SelectItem key={task.id} value={task.id}>{task.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload File
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Supported: PDF, Word, Excel, PowerPoint, Images, ZIP (max 50MB)
        </p>
      </div>

      {/* Files List */}
      <div className="card-clinical p-4">
        {files.length > 0 ? (
          <div className="space-y-2">
            {files.map((file) => {
              const FileIcon = getFileIcon(file.mime_type);
              const linkedTask = tasks.find(t => t.id === file.task_id);
              return (
                <div key={file.id} className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg hover:bg-slate-800/50">
                  <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <FileIcon className="w-5 h-5 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{file.original_name}</p>
                    <p className="text-xs text-slate-500">
                      {formatFileSize(file.size)} • {file.uploaded_by_name} • {new Date(file.uploaded_at).toLocaleDateString()}
                      {linkedTask && <span className="text-blue-400"> • {linkedTask.name}</span>}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDownload(file)}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <File className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No files uploaded yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

// =====================
// TEAM TAB COMPONENT
// =====================
const TeamTab = ({ projectId, project, organizations, token, onRefresh }) => {
  const [invites, setInvites] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignType, setAssignType] = useState("contractor"); // contractor or supplier
  const [availableContractors, setAvailableContractors] = useState([]);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      // Fetch invites
      const invRes = await fetch(`${API}/contractors/invites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (invRes.ok) {
        const data = await invRes.json();
        setInvites(data.invites.filter(inv => inv.project_ids?.includes(projectId)));
      }
      
      // Fetch all contractors
      const contRes = await fetch(`${API}/contractors/profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (contRes.ok) {
        const data = await contRes.json();
        setAvailableContractors(data.contractors || []);
        // Filter assigned contractors
        const assignedIds = project?.assigned_contractors || [];
        setContractors(data.contractors?.filter(c => assignedIds.includes(c.id)) || []);
      }
      
      // Fetch all suppliers
      const suppRes = await fetch(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (suppRes.ok) {
        const data = await suppRes.json();
        setAvailableSuppliers(data.suppliers || []);
        const assignedIds = project?.assigned_suppliers || [];
        setSuppliers(data.suppliers?.filter(s => assignedIds.includes(s.id)) || []);
      }
    } catch (error) {
      console.error("Error fetching team data:", error);
    } finally {
      setLoading(false);
    }
  };

  const assignToProject = async (type, id) => {
    try {
      const field = type === "contractor" ? "assigned_contractors" : "assigned_suppliers";
      const current = project?.[field] || [];
      
      if (current.includes(id)) {
        toast.error("Already assigned to this project");
        return;
      }
      
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ [field]: [...current, id] })
      });
      
      if (response.ok) {
        toast.success(`${type === "contractor" ? "Contractor" : "Supplier"} assigned to project`);
        setShowAssignModal(false);
        fetchTeamData();
        onRefresh();
      }
    } catch (error) {
      toast.error("Failed to assign");
    }
  };

  const removeFromProject = async (type, id) => {
    try {
      const field = type === "contractor" ? "assigned_contractors" : "assigned_suppliers";
      const current = project?.[field] || [];
      
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ [field]: current.filter(x => x !== id) })
      });
      
      if (response.ok) {
        toast.success("Removed from project");
        fetchTeamData();
        onRefresh();
      }
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const clientOrg = organizations.find(org => org.id === project?.client_org_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-400" />
          Project Team
        </h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { setAssignType("contractor"); setShowAssignModal(true); }} className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10">
            <HardHat className="w-4 h-4 mr-2" />
            Assign Contractor
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setAssignType("supplier"); setShowAssignModal(true); }} className="border-green-500/50 text-green-400 hover:bg-green-500/10">
            <Truck className="w-4 h-4 mr-2" />
            Assign Supplier
          </Button>
        </div>
      </div>

      {/* Client (Hospital) */}
      {clientOrg && (
        <div className="card-clinical p-4">
          <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Client
          </h3>
          <div className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-white">{clientOrg.name}</p>
              <p className="text-sm text-slate-400">{clientOrg.contact_name}</p>
              <p className="text-xs text-slate-500">{clientOrg.contact_email}</p>
            </div>
            <span className="px-2 py-1 text-xs rounded-full bg-blue-500/20 text-blue-400">
              Hospital Client
            </span>
          </div>
        </div>
      )}

      {/* Assigned Contractors */}
      <div className="card-clinical p-4">
        <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
          <HardHat className="w-4 h-4" /> Assigned Contractors ({contractors.length})
        </h3>
        <div className="space-y-2">
          {contractors.length > 0 ? (
            contractors.map((cont) => (
              <div key={cont.id} className="flex items-center gap-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <HardHat className="w-5 h-5 text-orange-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-white">{cont.company_name}</p>
                  <p className="text-xs text-slate-400">{cont.name} • {cont.email}</p>
                  <div className="flex gap-1 mt-1">
                    {cont.categories?.slice(0, 3).map(cat => (
                      <span key={cat} className="px-1.5 py-0.5 text-xs rounded bg-slate-700 text-slate-300">
                        {cat.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${cont.is_compliant ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {cont.is_compliant ? 'Compliant' : 'Non-Compliant'}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => removeFromProject("contractor", cont.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-slate-500 text-sm text-center py-4">No contractors assigned. Click &quot;Assign Contractor&quot; to add.</p>
          )}
        </div>
      </div>

      {/* Assigned Suppliers */}
      <div className="card-clinical p-4">
        <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
          <Truck className="w-4 h-4" /> Assigned Suppliers ({suppliers.length})
        </h3>
        <div className="space-y-2">
          {suppliers.length > 0 ? (
            suppliers.map((supp) => (
              <div key={supp.id} className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-green-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-white">{supp.company_name}</p>
                  <p className="text-xs text-slate-400">{supp.contact_name} • {supp.email}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeFromProject("supplier", supp.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          ) : (
            <p className="text-slate-500 text-sm text-center py-4">No suppliers assigned. Click &quot;Assign Supplier&quot; to add.</p>
          )}
        </div>
      </div>

      {/* Pending Invites */}
      {invites.filter(inv => inv.status === "pending").length > 0 && (
        <div className="card-clinical p-4">
          <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
            <Mail className="w-4 h-4" /> Pending Invitations
          </h3>
          <div className="space-y-2">
            {invites.filter(inv => inv.status === "pending").map((invite) => (
              <div key={invite.id} className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <Mail className="w-5 h-5 text-yellow-400" />
                <div className="flex-1">
                  <p className="text-sm text-white">{invite.name}</p>
                  <p className="text-xs text-slate-400">{invite.email}</p>
                </div>
                <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-400">
                  Pending
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assign Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {assignType === "contractor" ? <HardHat className="w-5 h-5 text-orange-400" /> : <Truck className="w-5 h-5 text-green-400" />}
              Assign {assignType === "contractor" ? "Contractor" : "Supplier"}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {assignType === "contractor" ? (
              availableContractors.filter(c => !project?.assigned_contractors?.includes(c.id)).length > 0 ? (
                availableContractors.filter(c => !project?.assigned_contractors?.includes(c.id)).map(cont => (
                  <button
                    key={cont.id}
                    onClick={() => assignToProject("contractor", cont.id)}
                    className="w-full text-left p-3 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700 transition-colors"
                  >
                    <p className="font-medium text-white">{cont.company_name}</p>
                    <p className="text-xs text-slate-400">{cont.categories?.join(", ")}</p>
                  </button>
                ))
              ) : (
                <p className="text-center text-slate-500 py-4">All contractors already assigned</p>
              )
            ) : (
              availableSuppliers.filter(s => !project?.assigned_suppliers?.includes(s.id)).length > 0 ? (
                availableSuppliers.filter(s => !project?.assigned_suppliers?.includes(s.id)).slice(0, 20).map(supp => (
                  <button
                    key={supp.id}
                    onClick={() => assignToProject("supplier", supp.id)}
                    className="w-full text-left p-3 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700 transition-colors"
                  >
                    <p className="font-medium text-white">{supp.company_name}</p>
                    <p className="text-xs text-slate-400">{supp.email}</p>
                  </button>
                ))
              ) : (
                <p className="text-center text-slate-500 py-4">All suppliers already assigned</p>
              )
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Modal */}
      <ContractorInviteModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        projectId={projectId}
        organizations={organizations}
        token={token}
        onSuccess={() => {
          fetchTeamData();
          setShowInviteModal(false);
        }}
      />
    </div>
  );
};

// =====================
// CONTRACTOR INVITE MODAL
// =====================
const ContractorInviteModal = ({ open, onClose, projectId, organizations, token, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    org_id: "",
    message: ""
  });

  const contractorOrgs = organizations.filter(org => 
    org.org_type === "external_contractor" || org.org_type === "internal_contractor"
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/contractors/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          project_ids: [projectId]
        })
      });
      if (response.ok) {
        const data = await response.json();
        setInviteLink(data.invite_link);
        toast.success("Invitation sent successfully");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Error sending invitation");
    } finally {
      setSubmitting(false);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success("Link copied to clipboard");
  };

  const handleClose = () => {
    setInviteLink("");
    setFormData({ email: "", name: "", org_id: "", message: "" });
    onClose();
    if (inviteLink) onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-400" />
            Invite Contractor
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Send an invitation to a contractor to join this project
          </DialogDescription>
        </DialogHeader>

        {!inviteLink ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Contractor Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="contractor@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Contractor Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Organization *</Label>
              <Select value={formData.org_id} onValueChange={(v) => setFormData({ ...formData, org_id: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {contractorOrgs.map((org) => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {contractorOrgs.length === 0 && (
                <p className="text-xs text-yellow-400">
                  No contractor organizations found. <Link to="/admin/projects/organizations" className="underline">Add one first</Link>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Personal Message (Optional)</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                rows={3}
                placeholder="Add a personal message to the invitation..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={handleClose} className="text-slate-400">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !formData.org_id} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-center">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-emerald-400 font-medium">Invitation Sent!</p>
              <p className="text-slate-400 text-sm mt-1">
                An email has been sent to {formData.email}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Or share this link directly:</Label>
              <div className="flex gap-2">
                <Input
                  value={inviteLink}
                  readOnly
                  className="bg-slate-800 border-slate-700 text-white text-xs"
                />
                <Button onClick={copyInviteLink} variant="outline" className="border-slate-700 text-slate-300 shrink-0">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleClose} className="bg-blue-600 hover:bg-blue-700">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// =====================
// MAIN EXPORT
// =====================
export default function ProjectManagementPage() {
  return (
    <Routes>
      <Route index element={<ProjectDashboard />} />
      <Route path="organizations" element={<OrganizationsList />} />
      <Route path="list" element={<ProjectsList />} />
      <Route path="new" element={<NewProjectForm />} />
      <Route path=":projectId" element={<ProjectDetail />} />
      <Route path=":projectId/sow" element={<SOWDocumentPage />} />
    </Routes>
  );
}
